import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import PaymentMethodForm from '../components/PaymentMethodForm';
import PageSuggestion from '../components/PageSuggestion';
import { paymentMethodApi, bankAccountApi } from '../services/api';
import { PaymentMethod, BankAccount, CreatePaymentMethodRequest, UpdatePaymentMethodRequest } from '../types';

const PaymentMethods: React.FC = () => {
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [filteredPaymentMethods, setFilteredPaymentMethods] = useState<PaymentMethod[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPaymentMethod, setEditingPaymentMethod] = useState<PaymentMethod | null>(null);
    const [loading, setLoading] = useState(true);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchPaymentMethods();
        fetchBankAccounts();
    }, []);

    useEffect(() => {
        const filtered = paymentMethods.filter(method =>
            method.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredPaymentMethods(filtered);
        setCurrentPage(1);
    }, [searchTerm, paymentMethods]);

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            const response = await paymentMethodApi.getAll();
            setPaymentMethods(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar métodos de pagamento:', error);
            setPaymentMethods([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const response = await bankAccountApi.getAll();
            setBankAccounts(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar contas bancárias:', error);
            setBankAccounts([]);
        }
    };

    const handleCreate = async (data: CreatePaymentMethodRequest) => {
        try {
            await paymentMethodApi.create(data);
            await fetchPaymentMethods();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Erro ao criar método de pagamento:', error);
        }
    };

    const handleUpdate = async (data: UpdatePaymentMethodRequest) => {
        if (!editingPaymentMethod) return;

        try {
            await paymentMethodApi.update(editingPaymentMethod.id, data);
            await fetchPaymentMethods();
            setIsModalOpen(false);
            setEditingPaymentMethod(null);
        } catch (error) {
            console.error('Erro ao atualizar método de pagamento:', error);
        }
    };

    const handleEdit = (paymentMethod: PaymentMethod) => {
        setEditingPaymentMethod(paymentMethod);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingPaymentMethod(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPaymentMethod(null);
    };

    const handleSubmit = async (data: CreatePaymentMethodRequest | UpdatePaymentMethodRequest) => {
        if (editingPaymentMethod) {
            await handleUpdate(data as UpdatePaymentMethodRequest);
        } else {
            await handleCreate(data as CreatePaymentMethodRequest);
        }
    };

    const getBankAccountName = (bankAccountId: string) => {
        const account = bankAccounts.find(acc => acc.id === bankAccountId);
        return account ? account.name : 'Conta não encontrada';
    };

    const columns = [
        { key: 'description', label: 'Descrição' },
        {
            key: 'bankAccountId',
            label: 'Conta Bancária',
            render: (value: string) => getBankAccountName(value)
        },
        {
            key: 'isForPayment',
            label: 'Para Pagamento',
            render: (value: boolean) => value ? 'Sim' : 'Não'
        },
        {
            key: 'isForPayout',
            label: 'Para Recebimento',
            render: (value: boolean) => value ? 'Sim' : 'Não'
        },
        {
            key: 'createdAt',
            label: 'Criado em',
            render: (value: string) => new Date(value).toLocaleDateString('pt-BR')
        }
    ];

    const totalPages = Math.ceil(filteredPaymentMethods.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredPaymentMethods.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-secondary-600">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Métodos de Pagamento</h1>
                    <p className="text-secondary-600">Gerencie seus métodos de pagamento</p>
                    <PageSuggestion>
                        Configure cartoes, PIX, boletos ou outras formas de pagamento para reutilizar esses dados nas contas a pagar e a receber.
                    </PageSuggestion>
                </div>
                <button
                    onClick={handleAddNew}
                    className="btn-primary flex items-center justify-center"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Método
                </button>
            </div>

            <DataTable
                data={paginatedData}
                columns={columns}
                searchPlaceholder="Buscar por descrição..."
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onEdit={handleEdit}
                editButtonText="Editar"
            />

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingPaymentMethod ? 'Editar Método de Pagamento' : 'Adicionar Método de Pagamento'}
            >
                <PaymentMethodForm
                    paymentMethod={editingPaymentMethod}
                    bankAccounts={bankAccounts}
                    onSubmit={handleSubmit}
                    onCancel={handleCloseModal}
                />
            </Modal>
        </div>
    );
};

export default PaymentMethods;
